import React, { ReactElement, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { AddressSelectionContext } from '../../AddressSelectionProvider';
import { SecondaryOutline as OutlineButton } from '../../../button';
import { OrderType } from '../../../../types';
import { mediaQuery } from '../../../../theme';
import { useModalState } from '../../../modal/useModalState';
import { Webshop } from '@kotipizzagroup/kotipizza-shared-types';
import { useRestaurantById } from '../../useRestaurants';
import { isRestaurantOpenForDeliveryType } from '../restaurant-list/helpers';
import { useAgeRestrictedProducts } from '../../../../hooks/useAgeRestrictedProducts';
import { TextInfoBox } from '../../../infobox/TextInfoBox';
import { ADDRESS_SELECTION_MODAL_NAME } from '../..';
import { Delivery } from '../../../../icons/Delivery';
import { Pickup } from '../../../../icons/Pickup';
import { Eatin } from '../../../../icons/Eatin';
import { ThreeDotsLoader } from '../../../loading/ThreeDotsLoader';
import { colors } from '../../../../theme/color';

type Props = {
  isHomepage?: boolean;
  restaurantId?: number;
  outlinedIcons?: boolean;
};

export const OrderMethods: React.FC<Props> = ({
  restaurantId,
  isHomepage = false,
  outlinedIcons = false,
  ...props
}) => {
  const {
    orderType: currentOrderType,
    setOrderType,
    setIsPreorder,
  } = useContext(AddressSelectionContext);
  const { openModal } = useModalState();
  const { restaurant } = useRestaurantById(restaurantId);
  const { orderContainsAlcohol } = useAgeRestrictedProducts();

  const orderTypes = useMemo(() => {
    if (!restaurant && restaurantId) return null;
    if (restaurant) {
      return [
        isRestaurantOpenForDeliveryType(restaurant, OrderType.DELIVERY).isOpen
          ? OrderType.DELIVERY
          : null,
        isRestaurantOpenForDeliveryType(restaurant, OrderType.PICKUP).isOpen
          ? OrderType.PICKUP
          : null,
        isRestaurantOpenForDeliveryType(restaurant, OrderType.EATIN).isOpen
          ? OrderType.EATIN
          : null,
      ].filter(Boolean) as OrderType[];
    }

    return [OrderType.DELIVERY, OrderType.PICKUP, OrderType.EATIN].filter(Boolean) as OrderType[];
  }, [restaurant, restaurantId]);

  if (!orderTypes) {
    return (
      <ButtonRow {...props} isHomepage={isHomepage} id="address-selection-buttonrow">
        <LoadingContainer>
          <ThreeDotsLoader
            height="20px"
            alt="ladataan"
            fill={isHomepage ? colors.brandPrimary : colors.white}
            isHomepage={isHomepage}
          />
        </LoadingContainer>
      </ButtonRow>
    );
  }

  if (orderTypes.length < 1) {
    return (
      <>
        {restaurant?.physicalRestaurantStatus === Webshop.AvailabilityStatus.Open && (
          <p>Emme ota enää verkkokauppatilauksia tälle päivälle.</p>
        )}
        <StyledOutlineButton
          onClick={() => {
            if (restaurant?.openForDeliveryStatus === Webshop.AvailabilityStatus.Closed) {
              setOrderType(OrderType.DELIVERY);
            } else {
              setOrderType(OrderType.PICKUP);
            }

            setIsPreorder(true);
            openModal(ADDRESS_SELECTION_MODAL_NAME);
          }}
        >
          Tee ennakkotilaus
        </StyledOutlineButton>
      </>
    );
  }

  const iconOutlineStyle = {
    borderStyle: 'solid',
    borderRadius: '50%',
    borderWidth: '2px',
    padding: '4px',
  };

  return (
    orderTypes && (
      <>
        {orderContainsAlcohol && (
          <DeliveryRestriction>Emme kuljeta alkoholia toistaiseksi.</DeliveryRestriction>
        )}
        <ButtonRow
          {...props}
          isHomepage={isHomepage}
          role={!isHomepage ? 'tablist' : undefined}
          id="address-selection-buttonrow"
        >
          {orderTypes.map((orderType) => (
            <TypeButton
              key={orderType}
              onClick={() => {
                setOrderType(orderType);
                if (isHomepage) {
                  openModal(ADDRESS_SELECTION_MODAL_NAME);
                }
              }}
              aria-selected={!isHomepage ? orderType === currentOrderType : undefined}
              role={!isHomepage ? 'tab' : 'button'}
              data-testid="orderTypeButton"
              isSingleRestaurantPage={outlinedIcons}
            >
              <IconContainer
                active={!isHomepage && orderType === currentOrderType}
                isHomepage={isHomepage}
              >
                {renderIcon(
                  orderType,
                  {
                    width: '64px',
                    height: '64px',
                    ...(outlinedIcons && iconOutlineStyle),
                  },
                  !isHomepage && orderType === currentOrderType ? 'white' : colors.textDefault
                )}
              </IconContainer>
              <div>{getTitle(orderType)}</div>
            </TypeButton>
          ))}
        </ButtonRow>
      </>
    )
  );
};

export const renderIcon = (
  type: OrderType,
  style?: React.CSSProperties,
  color?: string
): ReactElement => {
  switch (type) {
    case OrderType.DELIVERY:
      return <Delivery color={color} aria-hidden="true" style={style} />;
    case OrderType.PICKUP:
      return <Pickup color={color} aria-hidden="true" style={style} />;
    case OrderType.EATIN:
    default:
      return <Eatin color={color} aria-hidden="true" style={style} />;
  }
};

export const getTitle = (orderType: OrderType): string => {
  switch (orderType) {
    case OrderType.DELIVERY:
      return 'Kuljetus';
    case OrderType.PICKUP:
      return 'Nouto';
    case OrderType.EATIN:
    default:
      return 'Paikan päällä';
  }
};

const StyledDeliveryIcon = styled(Delivery)``;
const StyledPickupIcon = styled(Pickup)``;
const StyledEatinIcon = styled(Eatin)``;

const TypeButton = styled.button<{ isSingleRestaurantPage?: boolean }>`
  border: 0;
  background: 0;

  @media ${mediaQuery.tablet} {
    min-width: 7.5rem;
  }

  min-width: ${({ isSingleRestaurantPage }) => (isSingleRestaurantPage ? '7.5rem' : '9rem')};
  border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
  color: ${colors.textDefault};

  &:focus-visible {
    outline: 3px solid ${colors.outlineDefault};
    outline-offset: 2px;
  }
`;

const ButtonRow = styled.div<{ isHomepage?: boolean }>`
  display: flex;
  padding-top: 8px;

  @media ${mediaQuery.medium} {
    justify-content: center;
  }

  @media ${({ isHomepage }) => (isHomepage ? mediaQuery.tiny : mediaQuery.medium)} {
    flex-direction: row;
    width: 100%;
    margin-bottom: 0;
  }
`;

const LoadingContainer = styled.div`
  height: 83px;
  width: 100%;
  display: flex;

  @media ${mediaQuery.small} {
    height: 80.5px;
  }
`;

const StyledOutlineButton = styled(OutlineButton)`
  width: auto;
  margin: auto;
`;

const DeliveryRestriction = styled(TextInfoBox)`
  color: ${colors.gray90};
  background-color: ${colors.yellowPrimary};
  margin-bottom: 16px;
`;

const IconContainer = styled.div<{ active?: boolean; isHomepage?: boolean }>`
  padding: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color?.button.primary};
  color: ${colors.textDefault};
  display: inline-block;
  transition: background-color 0.1s ease-in-out;

  &:hover {
    & ${StyledDeliveryIcon}, ${StyledPickupIcon}, ${StyledEatinIcon} {
      border-color: ${({ theme }) => (theme.isApp ? colors.black : colors.white)};
    }

    background-color: ${({ theme, isHomepage }) =>
      theme.isApp
        ? isHomepage
          ? theme.color?.button.primary
          : colors.green.lighter(60)
        : colors.green.darker(60)};

    & svg,
    & path,
    & line,
    & g {
      color: ${({ theme }) => (theme.isApp ? colors.textDefault : 'white')};
      stroke: ${({ theme }) => (theme.isApp ? colors.textDefault : 'white')};
    }
  }

  ${({ active }) =>
    active &&
    css`
      background-color: ${colors.green.darker(60)};

      & svg,
      & path,
      & line,
      & g {
        color: white;
        stroke: white;
      }

      &:hover {
        background-color: ${colors.green.darker(60)};

        & svg,
        & path,
        & line,
        & g {
          color: white;
          stroke: white;
        }
      }
    `}

  ${({ active, isHomepage }) =>
    !active &&
    !isHomepage &&
    css`
      background-color: ${colors.green.lighter(60)};
    `}
`;
